import { useMemo } from "react";
import { usePaneStore } from "../state/store-context";
import type { RowCheckResultEntry } from "../state/types";
import styles from "./RowCheckSummary.module.css";

/** One chip: the failing rows for a distinct rule (message) within a group. */
interface ChipModel {
  groupId: string;          // LineItemDefinition id (the grid key)
  groupName: string;
  label: string;            // the rule message (or a generic fallback)
  severity: "error" | "warn";
  rowNumbers: number[];     // failing rows, ascending
}

function buildChips(
  rowCheckResults: Record<string, RowCheckResultEntry[]>,
  nameByDefId: Map<string, string>,
): ChipModel[] {
  const chips: ChipModel[] = [];
  for (const [defId, entries] of Object.entries(rowCheckResults)) {
    // Group failing rows by their message so each authored rule gets one chip.
    const byLabel = new Map<string, ChipModel>();
    for (const e of entries) {
      if (e.passed) continue;
      const label = e.message ?? "Row check failed";
      const chip = byLabel.get(label) ?? {
        groupId: defId, groupName: nameByDefId.get(defId) ?? "Line items",
        label, severity: "warn" as "error" | "warn", rowNumbers: [],
      };
      if (e.severity === "error") chip.severity = "error";
      chip.rowNumbers.push(e.rowNumber);
      byLabel.set(label, chip);
    }
    for (const chip of byLabel.values()) {
      chip.rowNumbers.sort((a, b) => a - b);
      chips.push(chip);
    }
  }
  return chips;
}

/**
 * P2b — doc-panel summary of per-row rule-check failures. One chip per failing
 * rule per line-item group ("N rows failing <rule>"); clicking a chip focuses
 * the first failing row in the corresponding grid.
 */
export function RowCheckSummary() {
  const rowCheckResults = usePaneStore((s) => s.rowCheckResults);
  const lineItemDefinitions = usePaneStore((s) => s.lineItemDefinitions);
  const requestFocusRow = usePaneStore((s) => s.requestFocusRow);

  const chips = useMemo(() => {
    const nameByDefId = new Map(lineItemDefinitions.map((d) => [d.id, d.name]));
    return buildChips(rowCheckResults, nameByDefId);
  }, [rowCheckResults, lineItemDefinitions]);

  if (chips.length === 0) return null;

  return (
    <div className={styles.container} data-testid="rowcheck-summary" role="status" aria-live="polite">
      {chips.map((chip, i) => {
        const n = chip.rowNumbers.length;
        return (
          <button
            key={`${chip.groupId}-${i}`}
            type="button"
            data-testid={`rowcheck-chip-${chip.groupId}-${i}`}
            className={`${styles.chip} ${chip.severity === "error" ? styles.chipFail : styles.chipWarn}`}
            onClick={() => requestFocusRow(chip.groupId, chip.rowNumbers[0]!)}
            title={`${chip.groupName}: ${chip.label} — rows ${chip.rowNumbers.join(", ")}`}
          >
            <span className={styles.count}>{n}</span>
            <span>{n === 1 ? "row failing" : "rows failing"}: {chip.label}</span>
          </button>
        );
      })}
    </div>
  );
}
